import { ErrorMessage, SummaryList } from 'nhsuk-react-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { getCampaignContent } from 'utils/contentUtils.ts';
import { API_LOCK_CAMPAIGN_ERROR } from 'constants/errorMessages.ts';
import { CampaignConfig } from 'schemas/CampaignConfig.schema.ts';
import {
  conditionTargetToLabel,
  campaignTypeToLabel,
  YYYYMMDDToHumanReadable,
} from 'utils/formattingUtils.ts';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import useLockCampaignDetails from 'queries/mutate/lockCampaignDetails.ts';
import useUser from 'hooks/useUser.ts';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign.ts';
import { ICampaignLevelParams } from 'utils/parameterUtils.ts';
import { CampaignTemplate } from 'schemas/CampaignTemplates.schema.ts';
import SummaryRow from './SummaryRow.tsx';

interface CampaignSummaryProps {
  campaign: CampaignConfig;
  canUpdate: boolean;
  template?: CampaignTemplate;
}

export default function SummaryTable({ campaign, canUpdate, template }: CampaignSummaryProps) {
  const { getCampaignPathFor } = useCampaignNavigate();
  const { teamId, campaignId } = useParams() as ICampaignLevelParams;
  const user = useUser();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const lockCampaignDetails = useLockCampaignDetails();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });

  const campaignContent = getCampaignContent(campaign.CampaignConfig?.Type);

  const handleEdit = async (path: string) => {
    setError(null);
    try {
      if (!teamId || !campaignId) {
        setError('Cannot find team or campaign');
        return;
      }

      if (!(user.userId === campaign.locked_by?.id)) {
        await lockCampaignDetails.mutateAsync({ teamId, campaignId, newState: 'locked' });

        await fetchSpecificCampaign.refetch();
      }

      navigate(getCampaignPathFor(path));
    } catch {
      setError(API_LOCK_CAMPAIGN_ERROR);
    }
  };

  return (
    <>
      {
          error && <ErrorMessage>{error}</ErrorMessage>
      }
      <SummaryList>
        <SummaryRow
          itemName="Campaign name"
          itemKey="campaign-name"
          itemValue={campaign.CampaignConfig.Name}
          handleEdit={() => handleEdit('key-details')}
          canUpdate={canUpdate}
        />
        {
          template?.['CampaignConfig.Type.summary.display'] !== 'hidden' && (
          <SummaryRow
            itemName="Campaign type"
            itemKey="campaign-type"
            itemValue={campaign.CampaignConfig.Type && campaignTypeToLabel(campaign.CampaignConfig.Type)}
            handleEdit={() => handleEdit('key-details')}
            canUpdate={canUpdate && template?.['CampaignConfig.Type.input.display'] !== 'hidden'}
          />
          )
        }
        {
          template?.['CampaignConfig.Target.summary.display'] !== 'hidden' && (
          <SummaryRow
            itemName={`${campaignContent.type} type`}
            itemKey="campaign-target"
            itemValue={campaign.CampaignConfig.Target && conditionTargetToLabel(campaign.CampaignConfig.Target)}
            handleEdit={() => handleEdit('key-details')}
            canUpdate={canUpdate && template?.['CampaignConfig.Target.input.display'] !== 'hidden'}
          />
          )
        }
        {
          template?.['CampaignConfig.StartDate.summary.display'] !== 'hidden' && (
          <SummaryRow
            itemName={campaignContent.startDateLabel}
            itemKey="campaign-start-date"
            itemValue={campaign.CampaignConfig.StartDate && YYYYMMDDToHumanReadable(campaign.CampaignConfig.StartDate)}
            handleEdit={() => handleEdit('key-details')}
            canUpdate={canUpdate && template?.['CampaignConfig.StartDate.input.display'] !== 'hidden'}
          />
          )
        }
        {
          template?.['CampaignConfig.EndDate.summary.display'] !== 'hidden' && (
          <SummaryRow
            itemName={campaignContent.endDateLabel}
            itemKey="campaign-end-date"
            itemValue={campaign.CampaignConfig.EndDate && YYYYMMDDToHumanReadable(campaign.CampaignConfig.EndDate)}
            handleEdit={() => handleEdit('key-details')}
            canUpdate={canUpdate && template?.['CampaignConfig.EndDate.input.display'] !== 'hidden'}
          />
          )
        }
        {
          template?.['CampaignConfig.Manager.summary.display'] !== 'hidden' && (
          <SummaryRow
            itemName="Campaign manager"
            itemKey="campaign-manager"
            itemValue={campaign.CampaignConfig.Manager?.join(', ')}
            handleEdit={() => handleEdit('management-details')}
            canUpdate={canUpdate && template?.['CampaignConfig.Manager.input.display'] !== 'hidden'}
          />
          )
        }
        {
          template?.['CampaignConfig.Reviewer.summary.display'] !== 'hidden' && (
          <SummaryRow
            itemName="Campaign reviewer"
            itemKey="campaign-reviewer"
            itemValue={campaign.CampaignConfig.Reviewer?.join(', ')}
            handleEdit={() => handleEdit('management-details')}
            canUpdate={canUpdate && template?.['CampaignConfig.Reviewer.input.display'] !== 'hidden'}
          />
          )
        }
        {
          template?.['CampaignConfig.Approver.summary.display'] !== 'hidden' && (
          <SummaryRow
            itemName="Campaign approver"
            itemKey="campaign-approver"
            itemValue={campaign.CampaignConfig.Approver?.join(', ')}
            handleEdit={() => handleEdit('management-details')}
            canUpdate={canUpdate && template?.['CampaignConfig.Approver.input.display'] !== 'hidden'}
          />
          )
        }
        <SummaryRow
          itemName="Message plans"
          itemKey="message-plans"
          itemValue={campaign.CampaignConfig.AvailableRoutingIds && campaign.CampaignConfig.AvailableRoutingIds.length > 0
            ? `${campaign.CampaignConfig.AvailableRoutingIds.length} message plans ` : ''}
          handleEdit={() => handleEdit('message-plans')}
          canUpdate={canUpdate && template?.['CampaignConfig.AvailableRoutingIds.input.display'] !== 'hidden'}
        />
      </SummaryList>
    </>
  );
}
